import React, { useState, useEffect, useCallback, useRef } from 'react';
import { text } from '../text';
import { hooks } from '../hooks';
import { components } from '../components';
import { theme } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import axiosInstance from '../services/axiosInstance';
import {
  MdCheckCircle, MdCancel, MdAccessTime, MdDelete,
  MdEventNote, MdAccountBalanceWallet, MdEditCalendar,
} from 'react-icons/md';

const CheckCircleIcon = MdCheckCircle as any;
const CancelIcon = MdCancel as any;
const PendingIcon = MdAccessTime as any;
const DeleteIcon = MdDelete as any;
const LeaveIcon = MdEventNote as any;
const LoanIcon = MdAccountBalanceWallet as any;
const CorrectionIcon = MdEditCalendar as any;

type DocType = 'all' | 'leave' | 'correction' | 'loan';

interface DocItem {
  id: number;
  docType: 'leave' | 'correction' | 'loan';
  status: string;
  created_at: string;
  title: string;
  subtitle: string;
  canDelete: boolean;
  rawData: any;
}

const LIMIT = 15;

export const DocumentHistory: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const { t, language } = useLanguage();
  const td = (t as any).documentHistory || {};

  const [activeTab, setActiveTab] = useState<DocType>('all');
  const [items, setItems] = useState<DocItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const isFirstRunRef = useRef(true);

  const [confirmConfig, setConfirmConfig] = useState<{
    isOpen: boolean; message: string; onConfirm: () => void;
  }>({ isOpen: false, message: '', onConfirm: () => {} });

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean; title: string; message: string; type: 'success' | 'error';
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  const showAlert = (title: string, message: string, type: 'success' | 'error' = 'success') => {
    setAlertConfig({ isOpen: true, title, message, type });
  };

  const showConfirm = (message: string, onConfirm: () => void) => {
    setConfirmConfig({ isOpen: true, message, onConfirm });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
  };

  const mapLeave = (l: any): DocItem => {
    const leaveTypeMap: Record<string, string> = {
      'Paid Time Off (PTO)': td.annualLeave || 'Cuti Tahunan',
      'Sick Leave': td.sickLeave || 'Sakit',
      'Unpaid Leave': td.unpaidLeave || 'Absen',
      'Maternity Leave': td.maternityLeave || 'Melahirkan',
      'Marriage Leave': td.marriageLeave || 'Menikah',
    };
    return {
      id: l.id,
      docType: 'leave',
      status: l.status,
      created_at: l.created_at,
      title: leaveTypeMap[l.leave_type] || l.leave_type,
      subtitle: `${formatDate(l.start_date)} – ${formatDate(l.end_date)}`,
      canDelete: l.status === 'pending',
      rawData: l,
    };
  };

  const mapCorrection = (c: any): DocItem => ({
    id: c.id,
    docType: 'correction',
    status: c.status,
    created_at: c.created_at,
    title: td.correctionTitle || 'Koreksi Absensi',
    subtitle: formatDate(c.date),
    canDelete: c.status === 'pending',
    rawData: c,
  });

  const mapLoan = (l: any): DocItem => ({
    id: l.id,
    docType: 'loan',
    status: l.status,
    created_at: l.created_at,
    title: td.loanTitle || 'Kasbon / Pinjaman',
    subtitle: `${formatCurrency(l.amount)} · ${l.tenor} ${l.tenor_unit === 'weeks' ? (td.weeks || 'minggu') : (td.months || 'bulan')}`,
    canDelete: l.status === 'pending',
    rawData: l,
  });

  const fetchAll = useCallback(async (reset = false) => {
    if (reset) {
      offsetRef.current = 0;
      isFirstRunRef.current = true;
    }

    const currentOffset = offsetRef.current;
    if (currentOffset === 0) setIsLoading(true); else setIsLoadingMore(true);

    try {
      const promises: Promise<any>[] = [];

      if (activeTab === 'all' || activeTab === 'leave') {
        promises.push(axiosInstance.get(`/leaves?limit=${LIMIT}&offset=${currentOffset}&tab=mine`).then(r => r.data.map(mapLeave)).catch(() => []));
      } else {
        promises.push(Promise.resolve([]));
      }

      if (activeTab === 'all' || activeTab === 'correction') {
        promises.push(axiosInstance.get(`/attendance-corrections/my`).then(r => r.data.map(mapCorrection)).catch(() => []));
      } else {
        promises.push(Promise.resolve([]));
      }

      if (activeTab === 'all' || activeTab === 'loan') {
        promises.push(axiosInstance.get(`/loans?limit=${LIMIT}&offset=${currentOffset}`).then(r => r.data.map(mapLoan)).catch(() => []));
      } else {
        promises.push(Promise.resolve([]));
      }

      const [leaves, corrections, loans] = await Promise.all(promises);

      const merged: DocItem[] = [...leaves, ...corrections, ...loans].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      if (reset) {
        setItems(merged);
      } else {
        setItems(prev => {
          const existingIds = new Set(prev.map(x => `${x.docType}-${x.id}`));
          const newOnes = merged.filter(x => !existingIds.has(`${x.docType}-${x.id}`));
          return [...prev, ...newOnes];
        });
      }

      setHasMore(leaves.length === LIMIT || loans.length === LIMIT);
      offsetRef.current = currentOffset + LIMIT;
      isFirstRunRef.current = false;
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, language]);

  useEffect(() => {
    fetchAll(true);
  }, [fetchAll]);

  const handleDelete = async (item: DocItem) => {
    showConfirm(td.deleteConfirm || 'Yakin ingin membatalkan pengajuan ini?', async () => {
      setConfirmConfig(prev => ({ ...prev, isOpen: false }));
      try {
        if (item.docType === 'leave') await axiosInstance.delete(`/leaves/${item.id}`);
        else if (item.docType === 'correction') await axiosInstance.delete(`/attendance-corrections/${item.id}`);
        else if (item.docType === 'loan') await axiosInstance.delete(`/loans/${item.id}`);
        setItems(prev => prev.filter(x => !(x.id === item.id && x.docType === item.docType)));
        showAlert(t.common.success, td.deleteSuccess || 'Pengajuan berhasil dibatalkan.');
      } catch (err: any) {
        showAlert(t.common.error, err?.response?.data?.message || td.deleteError || 'Gagal membatalkan pengajuan.', 'error');
      }
    });
  };

  const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved': return { color: '#10B981', bg: '#D1FAE5', icon: <CheckCircleIcon size={14} />, label: td.approved || 'Disetujui' };
      case 'rejected': return { color: '#EF4444', bg: '#FEE2E2', icon: <CancelIcon size={14} />, label: td.rejected || 'Ditolak' };
      default: return { color: '#F59E0B', bg: '#FEF3C7', icon: <PendingIcon size={14} />, label: td.pending || 'Menunggu' };
    }
  };

  const getDocTypeConfig = (docType: string) => {
    switch (docType) {
      case 'leave': return { icon: <LeaveIcon size={20} color="#10B981" />, bg: '#ECFDF5', label: td.tabLeave || 'Cuti' };
      case 'correction': return { icon: <CorrectionIcon size={20} color="#3B82F6" />, bg: '#EFF6FF', label: td.tabCorrection || 'Koreksi' };
      case 'loan': return { icon: <LoanIcon size={20} color="#8B5CF6" />, bg: '#F5F3FF', label: td.tabLoan || 'Kasbon' };
      default: return { icon: null, bg: '#F3F4F6', label: '' };
    }
  };

  const tabs: { key: DocType; label: string }[] = [
    { key: 'all', label: td.tabAll || 'Semua' },
    { key: 'leave', label: td.tabLeave || 'Cuti' },
    { key: 'correction', label: td.tabCorrection || 'Koreksi' },
    { key: 'loan', label: td.tabLoan || 'Kasbon' },
  ];

  const displayedItems = activeTab === 'all'
    ? items
    : items.filter(x => x.docType === activeTab);

  return (
    <div id='screen'>
      <components.Header title={td.title || 'Riwayat Dokumen'} goBack={true} />

      <main className='container' style={{ marginTop: 60, paddingBottom: 24, height: 'calc(100vh - 60px)', overflowY: 'auto' }}>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flexShrink: 0,
                padding: '7px 16px',
                borderRadius: 20,
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: activeTab === tab.key ? '700' : '500',
                backgroundColor: activeTab === tab.key ? theme.colors.mainDark : '#F3F4F6',
                color: activeTab === tab.key ? '#fff' : '#6B7280',
                transition: 'all 0.2s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* List */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>
            <text.T14>{t.common?.loading || 'Memuat...'}</text.T14>
          </div>
        ) : displayedItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>
            <text.T14>{td.noData || 'Belum ada pengajuan.'}</text.T14>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {displayedItems.map(item => {
              const statusCfg = getStatusConfig(item.status);
              const typeCfg = getDocTypeConfig(item.docType);

              const handleNavigateDetail = () => {
                if (item.docType === 'leave') navigate('/LeaveDetails', { state: { leave: item.rawData } });
                else if (item.docType === 'correction') navigate('/CorrectionDetail', { state: { correction: item.rawData } });
                else if (item.docType === 'loan') navigate('/LoanDetail', { state: { loan: item.rawData } });
              };

              return (
                <div
                  key={`${item.docType}-${item.id}`}
                  onClick={handleNavigateDetail}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: 14,
                    padding: '14px 16px',
                    border: '1px solid #F3F4F6',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                  }}
                >
                  {/* Type Icon */}
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                    backgroundColor: typeCfg.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {typeCfg.icon}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <text.H5 style={{ fontSize: 14 }}>{item.title}</text.H5>
                    </div>
                    {item.docType === 'loan' && item.rawData.approved_amount &&
                      parseFloat(item.rawData.approved_amount) !== parseFloat(item.rawData.amount) ? (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: '700', color: '#10B981' }}>
                          {formatCurrency(parseFloat(item.rawData.approved_amount))}
                        </span>
                        <span style={{ fontSize: 11, color: '#9CA3AF', textDecoration: 'line-through' }}>
                          {formatCurrency(parseFloat(item.rawData.amount))}
                        </span>
                        <span style={{ fontSize: 12, color: '#6B7280' }}>
                          · {item.rawData.approved_tenor || item.rawData.tenor}{' '}
                          {(item.rawData.approved_tenor_unit || item.rawData.tenor_unit) === 'weeks'
                            ? (td.weeks || 'minggu') : (td.months || 'bulan')}
                        </span>
                      </div>
                    ) : (
                      <text.T14 style={{ color: '#6B7280', fontSize: 12, marginBottom: 4 }}>{item.subtitle}</text.T14>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        padding: '2px 8px', borderRadius: 20,
                        backgroundColor: statusCfg.bg, color: statusCfg.color,
                        fontSize: 11, fontWeight: '700',
                      }}>
                        {statusCfg.icon}
                        <span>{statusCfg.label}</span>
                      </div>
                      <text.T14 style={{ color: '#9CA3AF', fontSize: 11 }}>
                        {formatDate(item.created_at)}
                      </text.T14>
                    </div>
                  </div>

                  {/* Delete button (only pending) */}
                  {item.canDelete && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                      style={{
                        flexShrink: 0,
                        width: 36, height: 36, borderRadius: 10,
                        border: 'none', cursor: 'pointer',
                        backgroundColor: '#FEF2F2',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <DeleteIcon size={18} color="#EF4444" />
                    </button>
                  )}
                </div>
              );
            })}

            {/* Load more */}
            {hasMore && !isLoadingMore && (
              <button
                onClick={() => fetchAll(false)}
                style={{
                  width: '100%', padding: 12, borderRadius: 12,
                  border: '1px dashed #D1D5DB', backgroundColor: 'transparent',
                  cursor: 'pointer', color: '#6B7280', fontSize: 13,
                }}
              >
                {td.loadMore || 'Muat lebih banyak'}
              </button>
            )}
            {isLoadingMore && (
              <div style={{ textAlign: 'center', padding: 12, color: '#9CA3AF' }}>
                <text.T14>{t.common?.loading || 'Memuat...'}</text.T14>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Confirm Modal */}
      {confirmConfig.isOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 300, textAlign: 'center' }}>
            <text.H4 style={{ marginBottom: 10 }}>{td.confirmTitle || 'Konfirmasi'}</text.H4>
            <text.T14 style={{ color: '#6B7280', marginBottom: 24 }}>{confirmConfig.message}</text.T14>
            <div style={{ display: 'flex', gap: 10 }}>
              <components.Button
                title={t.common?.cancel || 'Batal'}
                colorScheme="light"
                containerStyle={{ flex: 1 }}
                onClick={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
              />
              <components.Button
                title={td.deleteBtn || 'Batalkan'}
                containerStyle={{ flex: 1 }}
                onClick={confirmConfig.onConfirm}
              />
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertConfig.isOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 300, textAlign: 'center' }}>
            <div style={{ marginBottom: 8 }}>
              {alertConfig.type === 'success'
                ? <CheckCircleIcon size={32} color="#10B981" />
                : <CancelIcon size={32} color="#EF4444" />
              }
            </div>
            <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
            <text.T14 style={{ color: '#6B7280', marginBottom: 20 }}>{alertConfig.message}</text.T14>
            <components.Button title="OK" onClick={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))} />
          </div>
        </div>
      )}
    </div>
  );
};
